import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAppContext } from '../context/AppContext'
import { SixDigitCodeInput } from './SixDigitCode'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import '../styles/Login.css'
import '../styles/Admin.css'
import '../styles/Global.css'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000'

function AdminCode() {
  const navigate = useNavigate()
  const { login } = useAppContext()

  const [username, setUsername] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [isNewPassword, setIsNewPassword] = useState(false)
  const [enteredCode, setEnteredCode] = useState('')
  const [codeKey, setCodeKey] = useState(0)
  const [token, setToken] = useState('')
  const [verifiedUsername, setVerifiedUsername] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [seconds, setSeconds] = useState(0)
  const maxSeconds = 120

  useEffect(() => {
    let timer
    if (isCodeSent && !isNewPassword) {
      timer = setInterval(() => {
        setSeconds((prev) => prev + 1)
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [isCodeSent, isNewPassword])

  const sendCode = async () => {
    setLoading(true)
    setError('')
    try {
      await axios.post(`${BASE_URL}/admin/send-code`, { username })
      setIsCodeSent(true)
      setSeconds(0)
    } catch (err) {
      setError('Failed to send code. Try again.')
    }
    setLoading(false)
  }

  const verifyCode = async (code) => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.post(`${BASE_URL}/admin/verify-code`, {
        username,
        code,
      })
      const { token: receivedToken, username: adminUsername } = response.data
      setToken(receivedToken)
      setVerifiedUsername(adminUsername)
      setIsNewPassword(true)
    } catch (err) {
      setError('הקוד שגוי, נסה שוב.')
      setEnteredCode('')
      setCodeKey((k) => k + 1)
    }
    setLoading(false)
  }

  const handleSetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError('Please fill in both password fields.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    setError('')
    try {
      await axios.post(
        `${BASE_URL}/admin/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const expiryTime = new Date().getTime() + 86400000
      localStorage.setItem('adminToken', token)
      localStorage.setItem('adminUsername', verifiedUsername)
      localStorage.setItem('adminTokenExpiry', expiryTime.toString())

      login({ token, username: verifiedUsername })
      navigate('/admin')
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error)
      } else {
        setError('Failed to set password. Try again.')
      }
    }
    setLoading(false)
  }

  return (
    <div className="admin-container">
      {!isCodeSent ? (
        <>
          <button
            onClick={() => navigate(-1)}
            className="admin-button"
            style={{ background: 'none', color: '#64748b', boxShadow: 'none', fontSize: '14px', width: 'auto', gap: '6px' }}
          >
            ← חזור
          </button>
          <h2 className="admin-title">שחזור סיסמה</h2>
          <input
            type="text"
            placeholder="שם משתמש"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <button
            onClick={sendCode}
            disabled={isLoading}
            className="admin-button"
          >
            {isLoading ? 'שולח...' : 'שלח קוד'}
          </button>
          {error && <div className="error-message">{error}</div>}
        </>
      ) : isNewPassword ? (
        <div className="login-form">
          <h2 style={{ textAlign: 'center', color: '#1e293b', marginBottom: '8px', fontSize: '22px' }}>
            סיסמה חדשה
          </h2>
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
            בחר סיסמה חדשה לחשבונך
          </p>

          <label>סיסמה חדשה</label>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="הזן סיסמה חדשה"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ paddingRight: '44px', width: '100%', boxSizing: 'border-box' }}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>

          <label>אימות סיסמה</label>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="הזן שוב את הסיסמה"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                paddingRight: '44px', width: '100%', boxSizing: 'border-box',
                borderColor: confirmPassword && confirmPassword !== newPassword ? '#ef4444' : undefined,
              }}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
          {confirmPassword && confirmPassword !== newPassword && (
            <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', textAlign: 'right' }}>
              הסיסמאות אינן תואמות
            </p>
          )}

          {error && <div className="error-message" style={{ marginTop: '12px' }}>{error}</div>}

          <button
            onClick={handleSetPassword}
            disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          >
            {isLoading ? 'שומר...' : 'שמור סיסמה'}
          </button>
        </div>
      ) : (
        <>
          <h2 className="admin-title">הזן קוד אימות</h2>
          <SixDigitCodeInput key={codeKey} onChange={setEnteredCode} />
          {error && <div className="error-message">{error}</div>}
          <button
            onClick={() => verifyCode(enteredCode)}
            disabled={enteredCode.length < 6 || isLoading}
            className="admin-button"
          >
            {isLoading ? 'מאמת...' : 'אמת קוד'}
          </button>
          <button
            onClick={sendCode}
            disabled={isLoading || seconds < maxSeconds}
            className="admin-button"
            style={{ marginTop: '8px' }}
          >
            {isLoading ? 'שולח...' : 'שלח קוד שוב'}
          </button>
          {seconds < maxSeconds && (
            <p className="resend-message">
              שלח שוב בעוד {maxSeconds - seconds} שניות
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default AdminCode
